/**
 * Document versioning (piece 22; decisions 0012, 0035, 0159, 0172, 0490): the
 * pure half. Versioning covers Quote, Sales Order, Invoice, Credit Note and,
 * since decision 0490, the Purchase Order; the
 * single trigger is Send; a version is a frozen deep copy under a format
 * version the renderer branches on; the list is headed by the revision where
 * the document carries one and by the version number where it does not; and
 * two versions compare side by side. The layout each format version draws is
 * declared here beside the number, and `tools/check-snapshot-layout.py` fails
 * a layout that moves without its version moving (piece 22, the Quote's
 * check rule).
 */
import type { PrintTerms } from '../settings/termContainers';
import type { FrozenDealTeam } from './frozenDealTeam';
import type { FrozenTermsRef } from './frozenTerms';

export const VERSIONED_DOCUMENT_TYPES = ['Quote', 'SalesOrder', 'Invoice', 'CreditNote', 'PurchaseOrder'] as const;
export type VersionedDocumentType = (typeof VERSIONED_DOCUMENT_TYPES)[number];

export function isVersioned(documentType: string): documentType is VersionedDocumentType {
  return (VERSIONED_DOCUMENT_TYPES as readonly string[]).includes(documentType);
}

/**
 * A Work Order is internal and never versioned. The Purchase Order was too
 * until decision 0490: it carried a revision number whose earlier sheets
 * nobody could open, which made the number a label rather than a history
 * (product owner, 2026-09-17).
 */
export function versioningRefusal(documentType: string): string | null {
  return isVersioned(documentType) ? null : `${documentType} is internal and never versioned; the set is ${VERSIONED_DOCUMENT_TYPES.join(', ')}`;
}

/**
 * The documents whose list is headed by a revision suffix; the Invoice and the
 * Credit Note carry none and list by version number.
 *
 * 🔨 The Purchase Order joined this list with decision 0490, when its sheets
 * became openable. Decision 0485 had given it the number alone, so its history
 * had nothing for the revision to head.
 */
export const REVISIONED_DOCUMENT_TYPES = ['Quote', 'SalesOrder', 'PurchaseOrder'] as const;

/**
 * The printed number of a document that carries a revision: base number,
 * hyphen, revision zero-padded to two digits (decision 0012). The Quote, the
 * Sales Order and, since decision 0485, the Purchase Order all print this way,
 * which is why it sits here rather than in any one document's rules.
 */
export function displayRevisionedNumber(baseNumber: string, revision: number): string {
  return `${baseNumber}-${String(Math.max(0, revision)).padStart(2, '0')}`;
}

/** A document that carries a revision in its printed number. */
export type RevisionedDocumentType = (typeof REVISIONED_DOCUMENT_TYPES)[number];

/**
 * 🔒 The number a person reads for a revisioned document, on every screen,
 * sheet and mail (decision 2830, the owner's answer C3 of 2026-10-10). A Quote
 * carries its suffix from revision zero, as Q-1042-00; a Sales Order and a
 * Purchase Order read bare until revised, and SO-1001-02 once revised.
 * 💰 Three rules stood until T2092: one order read PO-0042 on its page and
 * PO-0042-00 on the Item's tile.
 */
export function documentNumberText(documentType: RevisionedDocumentType, number: string, revision: number): string {
  return documentType === 'Quote' || revision > 0 ? displayRevisionedNumber(number, revision) : number;
}

/**
 * 🔒 The format version the renderer branches on, and the layout each version
 * draws. Raise the number when the layout changes; the check reads the layout
 * beside the number and refuses a layout that moved under the same version.
 */
export const SNAPSHOT_FORMAT_VERSION = 5;

export interface SnapshotLayout {
  header: readonly string[];
  line: readonly string[];
}

export const SNAPSHOT_LAYOUTS: Readonly<Record<number, SnapshotLayout>> = {
  1: {
    header: ['documentType', 'number', 'revision', 'companyName', 'billTo', 'shipTo', 'paymentTerms', 'documentDate', 'expiryDate', 'displayMode', 'taxExemptState', 'taxExemptCertificateExpiresOn', 'currencyCode', 'totalCents'],
    line: ['lineNumber', 'itemId', 'sku', 'description', 'quantity', 'unitName', 'unitPriceCents', 'discountType', 'discountValue', 'taxCategoryName', 'amountCents'],
  },
  // 🔒 T225 (decision 0703): the terms the document went out with, its own text or the sections and clauses in force.
  2: {
    header: ['documentType', 'number', 'revision', 'companyName', 'billTo', 'shipTo', 'paymentTerms', 'documentDate', 'expiryDate', 'displayMode', 'taxExemptState', 'taxExemptCertificateExpiresOn', 'currencyCode', 'totalCents', 'terms'],
    line: ['lineNumber', 'itemId', 'sku', 'description', 'quantity', 'unitName', 'unitPriceCents', 'discountType', 'discountValue', 'taxCategoryName', 'amountCents'],
  },
  // 🔒 T129 (decision 0765): the Deal's team as it stood when the document was created, frozen with the copy. Format 2 is still read.
  3: {
    header: ['documentType', 'number', 'revision', 'companyName', 'billTo', 'shipTo', 'paymentTerms', 'documentDate', 'expiryDate', 'displayMode', 'taxExemptState', 'taxExemptCertificateExpiresOn', 'currencyCode', 'totalCents', 'terms', 'dealTeam'],
    line: ['lineNumber', 'itemId', 'sku', 'description', 'quantity', 'unitName', 'unitPriceCents', 'discountType', 'discountValue', 'taxCategoryName', 'amountCents'],
  },
  // 🔒 T277 (decision 0773): the terms by reference, the ids of the append-only library's rows in force, in place of their words. Formats 1 to 3 are still read.
  4: {
    header: ['documentType', 'number', 'revision', 'companyName', 'billTo', 'shipTo', 'paymentTerms', 'documentDate', 'expiryDate', 'displayMode', 'taxExemptState', 'taxExemptCertificateExpiresOn', 'currencyCode', 'totalCents', 'termsRef', 'dealTeam'],
    line: ['lineNumber', 'itemId', 'sku', 'description', 'quantity', 'unitName', 'unitPriceCents', 'discountType', 'discountValue', 'taxCategoryName', 'amountCents'],
  },
  // 🔒 T288 (decision 0738): format 4 and the tax rows the document went out with, one per jurisdiction, so a reopened revision keeps its own. Formats 1 to 4 are still read.
  5: {
    header: ['documentType', 'number', 'revision', 'companyName', 'billTo', 'shipTo', 'paymentTerms', 'documentDate', 'expiryDate', 'displayMode', 'taxExemptState', 'taxExemptCertificateExpiresOn', 'currencyCode', 'totalCents', 'termsRef', 'dealTeam', 'taxRows'],
    line: ['lineNumber', 'itemId', 'sku', 'description', 'quantity', 'unitName', 'unitPriceCents', 'discountType', 'discountValue', 'taxCategoryName', 'amountCents'],
  },
};

/**
 * The fingerprint of each version's layout, written once by
 * `python3 tools/check-snapshot-layout.py --write` when the version is new.
 * A fingerprint that no longer matches its layout is the failure the check
 * exists for: somebody changed what a stored document draws and left the
 * number alone.
 */
export const SNAPSHOT_LAYOUT_FINGERPRINTS: Readonly<Record<number, string>> = {
  5: '922fce978607092344a57432d4039877533c4213',
  4: 'd904026382554221d17f8dfb91b522977fbf592d',
  3: '095d198820df9512f217c8fd1aeb8ecc61f10d57',
  1: 'bc86d1265014d8d0477b5e13ea5acc6b045133f5',
  2: '22dda640ef0c9b672e7160808e692d024281360c',
};

export interface SnapshotHeader {
  documentType: VersionedDocumentType;
  number: string;
  /** The stored revision on a Quote or a Sales Order; null on an Invoice and a Credit Note. */
  revision: number | null;
  companyName: string;
  billTo: string;
  shipTo: string | null;
  paymentTerms: string;
  documentDate: string;
  expiryDate: string | null;
  /** The parts-or-total axis a customer-facing document carries; null on a Purchase Order, which has none. */
  displayMode: string | null;
  /** Null on a Purchase Order: exemption is the customer's fact, and a PO has no customer. */
  taxExemptState: boolean | null;
  taxExemptCertificateExpiresOn: string | null;
  currencyCode: string;
  totalCents: number;
  /**
   * 🔒 Format 2 (decision 0703): the terms as the sheet printed them when the
   * copy was written, filled in by the version writer inside the sending
   * transaction. Absent from a format 1 copy.
   */
  terms?: PrintTerms | null;
  /**
   * 🔒 Format 4 (decision 0773): the terms by reference, the library rows in force
   * by id and any override's words, filled in by the version writer inside the
   * sending transaction. Absent from a format 1, 2 or 3 copy, which reads `terms`.
   */
  termsRef?: FrozenTermsRef | null;
  /**
   * 🔒 Format 3 (decision 0765): the Deal's team as it stood when the document was
   * created, filled in by the version writer from the document's own frozen
   * column. Null on a document with no Deal; absent from a format 1 or 2 copy.
   */
  dealTeam?: FrozenDealTeam | null;
  /**
   * 🔒 Format 5 (decision 0738): the document's tax rows as it stored them
   * when the copy was written, one per jurisdiction in the order it draws
   * them, filled in by the version writer inside the sending transaction.
   * Null on a Purchase Order, which stores none; absent from a format 1 to 4
   * copy.
   */
  taxRows?: SnapshotTaxRow[] | null;
}

/**
 * One jurisdiction's tax as a frozen copy keeps it: the label the document
 * read, the rate, the base it was charged on and what it came to. It names no
 * live row, because a copy is never a pointer (decision 0159).
 */
export interface SnapshotTaxRow {
  level: string;
  label: string;
  ratePercent: number;
  taxableBaseCents: number;
  taxCents: number;
}

export interface SnapshotLine {
  lineNumber: number;
  itemId: string | null;
  sku: string | null;
  description: string;
  quantity: number;
  unitName: string;
  unitPriceCents: number;
  discountType: string | null;
  discountValue: number | null;
  taxCategoryName: string;
  amountCents: number;
}

/** What a DocumentVersion row's `snapshotData` holds: an untyped deep copy, read one row at a time, under its format version. */
export interface DocumentSnapshot {
  snapshotFormatVersion: number;
  header: SnapshotHeader;
  lines: SnapshotLine[];
}

const ordered = (keys: readonly string[], value: Record<string, unknown>): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const key of keys) out[key] = value[key] === undefined ? null : value[key];
  return out;
};

/** A frozen deep copy in the current layout's key order, never a pointer at the live row; a key the layout does not name is dropped. */
export function documentSnapshot(header: SnapshotHeader, lines: readonly SnapshotLine[], formatVersion: number = SNAPSHOT_FORMAT_VERSION): DocumentSnapshot {
  const layout = SNAPSHOT_LAYOUTS[formatVersion];
  if (!layout) throw new RangeError(`no snapshot layout is declared for format version ${formatVersion}`);
  const copy = JSON.parse(JSON.stringify({ header, lines })) as { header: SnapshotHeader; lines: SnapshotLine[] };
  return {
    snapshotFormatVersion: formatVersion,
    header: ordered(layout.header, copy.header as unknown as Record<string, unknown>) as unknown as SnapshotHeader,
    lines: copy.lines.map((line) => ordered(layout.line, line as unknown as Record<string, unknown>) as unknown as SnapshotLine),
  };
}

/** True where a snapshot's keys are exactly its format version's layout; the unit test holds the builder to the declaration. */
export function snapshotMatchesLayout(snapshot: DocumentSnapshot): boolean {
  const layout = SNAPSHOT_LAYOUTS[snapshot.snapshotFormatVersion];
  if (!layout) return false;
  const same = (keys: readonly string[], value: Record<string, unknown>) => JSON.stringify(Object.keys(value)) === JSON.stringify([...keys]);
  return same(layout.header, snapshot.header as unknown as Record<string, unknown>) && snapshot.lines.every((line) => same(layout.line, line as unknown as Record<string, unknown>));
}

/** Version 1 is written the first time a document reaches Sent; every Send writes the next and overwrites nothing. */
export function nextVersionNumber(existing: readonly number[]): number {
  return existing.length === 0 ? 1 : Math.max(...existing) + 1;
}

/**
 * The list is headed by the revision where the document carries one, never
 * the internal version number: the customer says "the second quote you sent
 * me" about 123-01, and a list headed Version 2 makes them agree on the wrong
 * copy. An Invoice and a Credit Note carry none and list by version number.
 */
export function versionHeading(documentType: string, versionNumber: number, header: Pick<SnapshotHeader, 'number' | 'revision'>): string {
  if ((REVISIONED_DOCUMENT_TYPES as readonly string[]).includes(documentType) && header.revision !== null) return documentNumberText(documentType as RevisionedDocumentType, header.number, header.revision);
  return `${header.number} · version ${versionNumber}`;
}

/** `revision` equals versionNumber − 1 only while nothing has been reopened and abandoned; the stored integer is the one that counts. */
export function revisionIfNothingAbandoned(versionNumber: number): number {
  return Math.max(0, versionNumber - 1);
}

export interface FieldDifference {
  field: string;
  from: unknown;
  to: unknown;
}

export interface LineDifference {
  lineNumber: number;
  /** 'added' and 'removed' name a whole line; 'changed' carries the fields that moved. */
  kind: 'added' | 'removed' | 'changed';
  fields: FieldDifference[];
}

export interface SnapshotComparison {
  header: FieldDifference[];
  lines: LineDifference[];
  same: boolean;
}

const equal = (a: unknown, b: unknown): boolean => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** The keys in the declared layout's order first, then any the layout does not name: a jsonb column keeps no key order of its own. */
function keysInLayoutOrder(declared: readonly string[] | undefined, ...objects: readonly Record<string, unknown>[]): string[] {
  return [...new Set([...(declared ?? []), ...objects.flatMap((o) => Object.keys(o))])];
}

/** Two versions side by side: the header fields that moved, and each line added, removed or changed by line number, in the layout's own order. */
export function compareSnapshots(a: DocumentSnapshot, b: DocumentSnapshot): SnapshotComparison {
  const layout = SNAPSHOT_LAYOUTS[a.snapshotFormatVersion] ?? SNAPSHOT_LAYOUTS[b.snapshotFormatVersion];
  const headerKeys = keysInLayoutOrder(layout?.header, a.header as unknown as Record<string, unknown>, b.header as unknown as Record<string, unknown>);
  const header: FieldDifference[] = headerKeys
    .filter((key) => !equal((a.header as unknown as Record<string, unknown>)[key], (b.header as unknown as Record<string, unknown>)[key]))
    .map((key) => ({ field: key, from: (a.header as unknown as Record<string, unknown>)[key] ?? null, to: (b.header as unknown as Record<string, unknown>)[key] ?? null }));
  const lines: LineDifference[] = [];
  const byNumber = (rows: readonly SnapshotLine[]) => new Map(rows.map((row) => [row.lineNumber, row]));
  const left = byNumber(a.lines);
  const right = byNumber(b.lines);
  const numbers = [...new Set([...left.keys(), ...right.keys()])].sort((x, y) => x - y);
  for (const lineNumber of numbers) {
    const before = left.get(lineNumber);
    const after = right.get(lineNumber);
    if (before && !after) lines.push({ lineNumber, kind: 'removed', fields: [] });
    else if (!before && after) lines.push({ lineNumber, kind: 'added', fields: [] });
    else if (before && after) {
      const keys = keysInLayoutOrder(layout?.line, before as unknown as Record<string, unknown>, after as unknown as Record<string, unknown>).filter((key) => key !== 'lineNumber');
      const fields = keys
        .filter((key) => !equal((before as unknown as Record<string, unknown>)[key], (after as unknown as Record<string, unknown>)[key]))
        .map((key) => ({ field: key, from: (before as unknown as Record<string, unknown>)[key] ?? null, to: (after as unknown as Record<string, unknown>)[key] ?? null }));
      if (fields.length > 0) lines.push({ lineNumber, kind: 'changed', fields });
    }
  }
  return { header, lines, same: header.length === 0 && lines.length === 0 };
}
